import { Controller } from '@hotwired/stimulus';

/**
 * Upload Type Selector Controller
 *
 * Manages the selection between "invoice" and "TS quote" upload types
 * on the contractor portal hub page. Handles unlocking the upload section,
 * switching the form action URL, toggling type-specific fields, and updating
 * the file input attributes.
 *
 * Works alongside the `radio-card` controller (visual card selection)
 * and the `file-upload` controller (drag-and-drop / file validation).
 *
 * Targets:
 * - uploadSection: The container that is disabled (greyed out) until a type is chosen
 * - form: The <form> element whose action URL is switched
 * - fileInput: The shared <input type="file"> whose name and accept attributes change
 * - invoiceField: Elements visible only when "invoice" is selected
 * - tsField: Elements visible only when "TS quote" is selected
 * - uploadHeading: Heading text updated to reflect the selected type
 * - uploadSubheading: Subheading text updated to reflect accepted formats
 *
 * Values:
 * - invoiceAction: POST URL for invoice uploads
 * - tsAction: POST URL for TS quote submissions
 * - invoiceCheckDuplicateUrl: URL for duplicate file detection (invoice only)
 *
 * Actions:
 * - selectInvoice: Activates invoice mode
 * - selectTs: Activates TS quote mode
 *
 * @example
 * <div data-controller="upload-type-selector"
 *      data-upload-type-selector-invoice-action-value="/facture/url"
 *      data-upload-type-selector-ts-action-value="/ts/url"
 *      data-upload-type-selector-invoice-check-duplicate-url-value="/check-duplicate/url">
 *   <label data-action="click->upload-type-selector#selectInvoice">Invoice</label>
 *   <label data-action="click->upload-type-selector#selectTs">TS Quote</label>
 *   <div data-upload-type-selector-target="uploadSection" class="opacity-40 pointer-events-none select-none">
 *     <form data-upload-type-selector-target="form" data-controller="file-upload">
 *       <input data-upload-type-selector-target="fileInput" type="file">
 *       <div data-upload-type-selector-target="invoiceField">Invoice-only field</div>
 *       <div data-upload-type-selector-target="tsField" class="hidden">TS-only field</div>
 *     </form>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'uploadSection',
    'form',
    'fileInput',
    'invoiceField',
    'tsField',
    'uploadHeading',
    'uploadSubheading',
    'submitButton',
    'commentInput',
    'customNameInput',
  ];

  static values = {
    invoiceAction: String,
    tsAction: String,
    invoiceCheckDuplicateUrl: String,
    preselect: String,
  };

  connect() {
    if (!this.preselectValue) return;
    // Defer so the radio-card controller is fully connected
    requestAnimationFrame(() => {
      const input = this.element.querySelector(
        `input[type="radio"][value="${this.preselectValue}"]`
      );
      input?.closest('label')?.click();
    });
  }

  selectInvoice() {
    this.#enable();
    this.formTarget.action = this.invoiceActionValue;
    this.fileInputTarget.name = 'invoice_document[file]';
    this.fileInputTarget.accept = '.pdf,.jpg,.jpeg,.png';

    // Enable duplicate check for invoices
    this.formTarget.dataset.fileUploadCheckDuplicateUrlValue = this.invoiceCheckDuplicateUrlValue;

    // Restore comment field name for invoice
    if (this.hasCommentInputTarget) {
      this.commentInputTarget.name = 'invoice_document[contractor_comment]';
    }

    // Restore custom name field name for invoice
    if (this.hasCustomNameInputTarget) {
      this.customNameInputTarget.name = 'invoice_document[custom_name]';
    }

    // Show invoice fields, hide TS fields
    this.invoiceFieldTargets.forEach((el) => el.classList.remove('hidden'));
    this.tsFieldTargets.forEach((el) => el.classList.add('hidden'));

    // Update section texts
    if (this.hasUploadHeadingTarget) {
      this.uploadHeadingTarget.textContent = 'Déposer une facture';
    }
    if (this.hasUploadSubheadingTarget) {
      this.uploadSubheadingTarget.textContent = 'PDF, JPEG ou PNG (max 10 Mo)';
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.textContent = 'Envoyer la facture';
    }
  }

  selectTs() {
    this.#enable();
    this.formTarget.action = this.tsActionValue;
    this.fileInputTarget.name = 'ts_quote_submission[contractor_quote]';
    this.fileInputTarget.accept = '.pdf';

    // No duplicate check for TS quotes
    this.formTarget.dataset.fileUploadCheckDuplicateUrlValue = '';

    // Switch comment field name for TS submission
    if (this.hasCommentInputTarget) {
      this.commentInputTarget.name = 'ts_quote_submission[contractor_comment]';
    }

    // Switch custom name field name for TS submission
    if (this.hasCustomNameInputTarget) {
      this.customNameInputTarget.name = 'ts_quote_submission[custom_name]';
    }

    // Show TS fields, hide invoice fields
    this.invoiceFieldTargets.forEach((el) => el.classList.add('hidden'));
    this.tsFieldTargets.forEach((el) => el.classList.remove('hidden'));

    // Update section texts
    if (this.hasUploadHeadingTarget) {
      this.uploadHeadingTarget.textContent = 'Déposer votre devis TS';
    }
    if (this.hasUploadSubheadingTarget) {
      this.uploadSubheadingTarget.textContent = 'PDF uniquement (max 10 Mo)';
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.textContent = 'Envoyer le devis';
    }
  }

  /**
   * Removes the disabled/greyed-out state from the upload section.
   * Called once when the user first selects a type.
   */
  #enable() {
    this.uploadSectionTarget.classList.remove('opacity-40', 'pointer-events-none', 'select-none');
  }
}
